import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '@/shared/ui';
import { hueOptionsList } from '@/shared/constants/hue-options';
import { CustomersController, CustomersStepTypeEnum, CustomersStepTypeEnumPaths } from '@/shared/endpoints/customers';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { RadioButton } from './radioButton';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES, ABSOLUTE_ROUTES } from '@/shared/constants/routes';
import { selectUserUserOnboarding } from '@/store/slices/onboarding/selectors';
import { smoothScrollTo } from '@/shared/helpers/scroll';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

interface IProps {
    proceedToNextStage: () => void;
    menuHeight: number;
}

const MedboxHue = ({ proceedToNextStage, menuHeight }: IProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const saveRef = useRef<HTMLButtonElement | null>(null);
    const userOnboarding = useAppSelector(selectUserUserOnboarding);
    const medboxHueType = userOnboarding?.details?.medboxHueType;
    const isFromReviewPage = Boolean(searchParams.get('fromReview'));
    const [selectedHueId, setSelectedHueId] = useState<number | null>(medboxHueType ?? null);
    const [isLoading, setIsLoading] = useState(false);
    const isFromReview = searchParams.get('from');

    if (!userOnboarding?.details) return null;

    const onUpdateHue = async () => {
        if (!userOnboarding?.details) return null;
        if (typeof selectedHueId !== 'number') return toast.error('Please select your preferred hue');
        setIsLoading(true);
        const payload = {
            ...userOnboarding,
            details: {
                ...(userOnboarding.details || {}),
                medboxHueType: selectedHueId,
                currentStep: isFromReviewPage ? CustomersStepTypeEnum.Review : userOnboarding.details.currentStep
            }
        };

        const res = await dispatch(CustomersController.updateMetadata(payload));
        setIsLoading(false);

        if (!CustomersController.updateMetadata.fulfilled.match(res)) {
            if (navigator.onLine) {
                toast.error('Something went wrong. Please try again.');
            }
            return;
        }

        if (isFromReviewPage) navigate(ROUTES.ONBOARDING.MAIN.replace(':stepType?', CustomersStepTypeEnumPaths.REVIEW));
        else {
            if (proceedToNextStage) {
                proceedToNextStage();
            } else {
                if (isFromReview) {
                    navigate(`/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.REVIEW}`);
                } else {
                    const defaultRoute = `/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.OFTEN}`;
                    navigate(defaultRoute);
                }
            }
        }
    };

    const handleScrollToSave = () => {
        if (isMobile && saveRef.current) {
            const rect = saveRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            smoothScrollTo(window.scrollY + (rect.y - viewportHeight + menuHeight + 100), 1000);
        }
    };

    const handleSelectHue = (id: number) => {
        setSelectedHueId(id);
        handleScrollToSave();
    };

    const onBack = () => {
        navigate(ABSOLUTE_ROUTES.ONBOARDING.INSURANCE.MAIN);
    }

    const isHueAlreadySelected = typeof medboxHueType === 'number';

    const title = isHueAlreadySelected ? "Let's pick" : "But first, let's pick";
    const saveButtonLabel = isHueAlreadySelected ? 'Save & Continue' : 'Start Filling My MedBox';

    return (
        <div className="flex flex-col items-center pb-[100px] pt-[25px] md:p-[2.75rem]" data-testid="medbox">
            <h1 data-testid="medbox-hue-title" className="my_medbox__heading mb-1 text-center font-agrandir text-2xl font-normal md:mb-4 md:text-5xl">{title}</h1>
            <h2 data-testid="medbox-hue-color-test" className="muiTheme mb-4 font-agrandir text-2xl text-primary-700 md:text-5xl">Your MedBox Color</h2>
            <p data-testid="medob-hue-subtitle" className="mb-8 text-center font-atkinsonHyperlegible text-base md:text-2xl">
                Please select which color lid you would like on your MedBox.
            </p>
            <div className="mb-8 flex flex-wrap items-center justify-center gap-2 sm:gap-4 md:gap-10">
                {hueOptionsList.map(hue => {
                    const isHueActive = hue.id === selectedHueId;

                    return (
                        <div
                            key={hue.id}
                            className={`group prescriptions__hue_${hue.name} ${isHueActive ? 'bg-primary-400' : 'bg-white'} ease relative flex w-full cursor-pointer flex-col items-center overflow-hidden rounded-3xl shadow-medbox-card transition duration-500 hover:scale-[1.035] hover:bg-primary-400 sm:w-56 md:w-[245px]`}
                            onClick={() => handleSelectHue(hue.id)}
                            data-testid={`medbox-hue-option-${hue.id}`}
                        >
                            <div className="relative h-44 w-full bg-[#e8e7e4] sm:h-64">
                                <img src={hue.src} className="absolute h-full w-full object-cover object-center" />
                            </div>
                            <RadioButton
                                color={hue.color}
                                isChecked={isHueActive}
                                className="absolute right-[9px] top-[9px] md:right-4 md:top-4"
                                onChange={() => handleSelectHue(hue.id)}
                            />
                            <span
                                data-testid="medbox-hue-option-name"
                                className={`${isHueActive ? 'text-white' : ''} ease w-full py-2 text-center font-dmSans text-lg transition duration-500 group-hover:text-white md:py-4 md:text-2xl`}
                            >
                                {hue.name}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-col gap-[15px] max-w-[27.375rem] w-full">
                <Button
                    ref={saveRef}
                    onClick={onUpdateHue}
                    className="btn btn-primary prescriptions__save_btn max-w-full"
                    showEndIcon
                    loading={isLoading}
                    testId="button-medbox-hue-start-filling"
                    fontClassName='font-bold'
                >
                    {saveButtonLabel}
                </Button>
                <Button
                    className="w-full"
                    variant="outlined"
                    onClick={onBack}
                    testId="button-medbox-hue-back"
                    fontClassName="font-dmSansMedium font-bold text-16 sm:text-18"
                >
                    Back
                </Button>
            </div>
        </div>
    );
};

export default withScrollRestoration(MedboxHue);
